import type { PortfolioCategory, PortfolioProject } from "@/hooks/usePortfolio";
import type { LandingShowcaseItem } from "./schema";

/** One website as the page shows it. */
export interface ShowcaseEntry {
  project: PortfolioProject;
  name: string;
  label: string;
}
/** A page's original selection: [website host, name shown, label shown]. */
export type BuiltInShowcase = readonly (readonly [string, string, string])[];

/** Fewest websites the section shows from an admin's list. */
export const MIN_SHOWCASE = 3;
/** Most websites the section shows. */
export const MAX_SHOWCASE = 6;

export function projectHost(url: string | null): string {
  try {
    return new URL(url || "").hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/** The original selection: the named sites first, then other published sites, up to six. */
export function builtInShowcase(
  builtIn: BuiltInShowcase,
  projects: PortfolioProject[],
  categories: PortfolioCategory[],
): ShowcaseEntry[] {
  const used = new Set<string>();
  const selected: ShowcaseEntry[] = [];
  for (const [domain, name, label] of builtIn) {
    const p = projects.find((x) => projectHost(x.project_url) === domain && x.image_url);
    if (p) {
      selected.push({ project: p, name, label });
      used.add(p.id);
    }
  }
  for (const p of projects) {
    const category = categories.find((c) => c.id === p.category_id);
    if (selected.length >= MAX_SHOWCASE) break;
    if (!used.has(p.id) && p.image_url && category?.slug !== "mobile-apps")
      selected.push({ project: p, name: p.title, label: category?.name ?? "Website" });
  }
  return selected;
}

/**
 * What the page shows. The admin's list is used when at least three of its
 * sites can still be shown (published, with an image, no repeats). Otherwise
 * the original selection is shown, so the section never looks half empty.
 */
export function resolveShowcase(
  builtIn: BuiltInShowcase,
  listed: LandingShowcaseItem[],
  projects: PortfolioProject[],
  categories: PortfolioCategory[],
): ShowcaseEntry[] {
  if (listed.length) {
    const chosen: ShowcaseEntry[] = [];
    for (const item of listed) {
      const p = projects.find((x) => x.id === item.id && x.image_url);
      if (!p || chosen.some((c) => c.project.id === p.id)) continue;
      const category = categories.find((c) => c.id === p.category_id);
      chosen.push({ project: p, name: item.name || p.title, label: item.label || category?.name || "Website" });
    }
    if (chosen.length >= MIN_SHOWCASE) return chosen.slice(0, MAX_SHOWCASE);
  }
  return builtInShowcase(builtIn, projects, categories);
}
